import React from 'react';
import {View, ScrollView, StyleSheet} from 'react-native';
import {Text, Chip} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import {useAuthStore} from '../../store/useAuthStore';
import GradientHeader from '../../components/common/GradientHeader';
import {colors, spacing} from '../../theme';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';

const DetailRow = ({label, value, icon}) => {
  if (!value) return null;
  return (
    <View style={styles.detailRow}>
      <View style={styles.detailLabelContainer}>
        {icon && <Icon name={icon} size={16} color={colors.textSecondary} style={{marginRight: 4}} />}
        <Text style={styles.detailLabel}>{label}</Text>
      </View>
      <Text style={styles.detailValue}>{value}</Text>
    </View>
  );
};

const ProfileScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {user} = useAuthStore();

  const isOwner = user?.owner || user?.isOwner;
  const roles = user?.roles ? [...user.roles] : [];
  const accessibleOrgs = user?.accessibleOrgs || [];
  const tenant = user?.tenant;

  return (
    <View style={styles.container}>
      <GradientHeader title={t('settings.profile')} onBack={() => navigation.goBack()} />

      <ScrollView contentContainerStyle={styles.scrollContent}>
        {/* Avatar & Name */}
        <View style={styles.avatarSection}>
          <View style={styles.avatar}>
            <Text style={styles.avatarText}>
              {(user?.fullName || '?').charAt(0).toUpperCase()}
            </Text>
          </View>
          <Text style={styles.name}>{user?.fullName}</Text>
          {isOwner && (
            <Chip style={styles.ownerChip} textStyle={styles.ownerChipText} compact>
              Owner
            </Chip>
          )}
        </View>

        {/* Personal Info */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Personal Information</Text>
          <DetailRow icon="email-outline" label={t('auth.email')} value={user?.email} />
          <DetailRow icon="phone-outline" label={t('auth.phone')} value={user?.phone ? `${user.countryCode || '+91'} ${user.phone}` : null} />
          <DetailRow icon="translate" label="Language" value={user?.preferredLang === 'en' ? 'English' : user?.preferredLang} />
        </View>

        {/* Account Info */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Account</Text>
          <DetailRow icon="store-outline" label="Business Name" value={tenant?.name} />
          <DetailRow icon="map-marker-outline" label="City" value={tenant?.city} />
          <DetailRow icon="map-outline" label="State" value={tenant?.state} />
        </View>

        {/* Roles */}
        {roles.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Roles</Text>
            <View style={styles.chipRow}>
              {roles.map(role => (
                <Chip key={role} style={styles.roleChip} textStyle={styles.roleChipText} compact>
                  {role}
                </Chip>
              ))}
            </View>
          </View>
        )}

        {/* Assigned Stores */}
        {accessibleOrgs.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Assigned Stores</Text>
            {accessibleOrgs.map((org, idx) => (
              <View key={org.orgUnitId || idx} style={styles.storeRow}>
                <View style={styles.storeInfo}>
                  <Icon name="store" size={16} color={colors.primary} />
                  <Text style={styles.storeName}>{org.orgName}</Text>
                </View>
                <View style={styles.storeRoles}>
                  {(org.roles || []).map(r => (
                    <Text key={r} style={styles.storeRoleText}>{r}</Text>
                  ))}
                  {org.canOperate && (
                    <View style={styles.activeDot} />
                  )}
                </View>
              </View>
            ))}
          </View>
        )}

        <View style={{height: spacing.xxl}} />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  scrollContent: {paddingBottom: spacing.md},

  // Avatar
  avatarSection: {
    alignItems: 'center',
    paddingVertical: spacing.lg,
    backgroundColor: colors.surface,
    marginBottom: spacing.sm,
  },
  avatar: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  avatarText: {color: colors.white, fontSize: 32, fontWeight: '700'},
  name: {fontSize: 20, fontWeight: '700', color: colors.text},
  ownerChip: {
    marginTop: spacing.xs,
    backgroundColor: colors.primary + '15',
  },
  ownerChipText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.primary,
  },

  // Sections
  section: {
    backgroundColor: colors.surface,
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.sm,
  },

  // Detail rows
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.divider,
  },
  detailLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  detailLabel: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  detailValue: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.text,
    textAlign: 'right',
    flex: 1,
    marginLeft: spacing.md,
  },

  // Chips
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  roleChip: {
    backgroundColor: colors.info + '15',
  },
  roleChipText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.info,
  },

  // Store rows
  storeRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.divider,
  },
  storeInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
  },
  storeName: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.text,
  },
  storeRoles: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  storeRoleText: {
    fontSize: 11,
    color: colors.textSecondary,
    fontWeight: '500',
  },
  activeDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.success,
  },
});

export default ProfileScreen;
